import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import './Warehouse.css';

export default function Warehouse() {
  const { t } = useLanguage();
  const [canIssue, setCanIssue] = useState(false);
  const [tab, setTab] = useState('stock');

  const [stock, setStock] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingStock, setLoadingStock] = useState(true);

  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [issueQty, setIssueQty] = useState({});   // { itemRowId: miqdar }
  const [issuing, setIssuing] = useState(false);
  const [err, setErr] = useState('');

  const loadStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const r = await API.get('/warehouse/stock', { params: search ? { q: search } : {} });
      setStock(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e.response?.data?.message || t('warehouse.loadError'));
    } finally { setLoadingStock(false); }
  }, [search, t]);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const r = await API.get('/warehouse/pending');
      setPending(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e.response?.data?.message || t('warehouse.loadError'));
    } finally { setLoadingPending(false); }
  }, [t]);

  useEffect(() => {
    API.get('/warehouse/access')
      .then(r => {
        const ok = !!r.data?.can_issue;
        setCanIssue(ok);
        if (ok) { setTab('pending'); loadPending(); }   // anbardar üçün iş növbəsi önə
      })
      .catch(() => setCanIssue(false));
  }, [loadPending]);
  useEffect(() => {
    const id = setTimeout(loadStock, search ? 300 : 0);   // yazarkən gözlə
    return () => clearTimeout(id);
  }, [loadStock, search]);

  // Tələbnaməni açanda miqdarları istənilən qədər doldururuq
  const toggleOpen = (r) => {
    if (openId === r.id) { setOpenId(null); return; }
    const init = {};
    r.items.forEach(i => {
      const max = i.on_hand == null ? i.quantity_requested : Math.min(i.quantity_requested, Math.max(0, i.on_hand));
      init[i.id] = max;
    });
    setIssueQty(init);
    setOpenId(r.id);
  };

  const submitIssue = async (r) => {
    const items = r.items.map(i => ({ id: i.id, quantity_released: Number(issueQty[i.id] || 0) }));
    const total = items.reduce((s, i) => s + i.quantity_released, 0);
    if (!total) { alert(t('warehouse.nothingToIssue')); return; }

    // Lazım olan tarix keçibsə gecikmə səbəbi olmadan mal verilə bilməz
    let note = null;
    const late = r.needed_by &&
      new Date() > new Date(new Date(r.needed_by).setHours(23, 59, 59, 999));
    if (late) {
      const due = new Date(r.needed_by).toLocaleDateString('az-AZ');
      for (;;) {
        const v = prompt(`${t('warehouse.lateTitle')} (${t('warehouse.lateDue')}: ${due}).\n${t('warehouse.lateAsk')}`, note || '');
        if (v === null) return;
        if (v.trim().length >= 3) { note = v.trim(); break; }
        alert(t('warehouse.lateTooShort'));
        note = v;
      }
    } else if (!window.confirm(`${r.request_number} — ${t('warehouse.confirmIssue')}`)) {
      return;
    }

    setIssuing(true);
    try {
      await API.post('/warehouse/issue/' + r.id, { items, note });
      setOpenId(null);
      await Promise.all([loadPending(), loadStock()]);
    } catch (e) {
      alert(e.response?.data?.message || t('warehouse.issueFailed'));
    } finally { setIssuing(false); }
  };

  const lowStock = stock.filter(s => s.available <= 0).length;

  // --- Excel yükləmə ---
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impMode, setImpMode] = useState('update');
  const [impPreview, setImpPreview] = useState(null);
  const [impBusy, setImpBusy] = useState(false);

  const resetImport = () => { setImpOpen(false); setImpFile(null); setImpPreview(null); setImpMode('update'); };

  const doPreview = async (file, mode) => {
    setImpBusy(true); setImpPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      const r = await API.post('/warehouse/import?preview=1', fd);
      setImpPreview(r.data);
    } catch (e) {
      alert(e.response?.data?.message || t('warehouse.importFailed'));
      setImpFile(null);
    } finally { setImpBusy(false); }
  };

  const doImport = async () => {
    if (!impFile) return;
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', impFile);
      fd.append('mode', impMode);
      const r = await API.post('/warehouse/import', fd);
      const s = r.data.summary;
      alert(`${t('warehouse.importDone')}\n\n${t('warehouse.impTotal')}: ${s.total}\n${t('warehouse.impNew')}: ${s.new_items}\n${t('warehouse.impChanged')}: ${s.changed}`);
      resetImport();
      await loadStock();
    } catch (e) {
      alert(e.response?.data?.message || t('warehouse.importFailed'));
    } finally { setImpBusy(false); }
  };

  // --- Tək material əlavəsi ---
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const emptyItem = { item_code: '', name: '', uom: 'ədəd', closing_qty: '', location: '' };
  const [newItem, setNewItem] = useState(emptyItem);

  const doAddItem = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      const r = await API.post('/warehouse/items', newItem);
      alert(r.data.updated ? t('warehouse.itemUpdated') : t('warehouse.itemAdded'));
      setAddOpen(false); setNewItem(emptyItem);
      await loadStock();
    } catch (err) {
      alert(err.response?.data?.message || t('warehouse.itemFailed'));
    } finally { setAddBusy(false); }
  };

  return (
    <div className="wh-page">
      <div className="wh-head">
        <div>
          <h2>{t('warehouse.title')}</h2>
          <p>{t('warehouse.subtitle')}</p>
        </div>
        <div className="wh-head-stats">
          <div className="wh-stat">
            <span className="wh-stat-num">{stock.length}</span>
            <span className="wh-stat-lbl">{t('warehouse.itemCount')}</span>
          </div>
          {canIssue && (
            <div className={"wh-stat" + (pending.length ? " accent" : "")}>
              <span className="wh-stat-num">{pending.length}</span>
              <span className="wh-stat-lbl">{t("warehouse.pendingCount")}</span>
            </div>
          )}
          {lowStock > 0 && (
            <div className="wh-stat danger">
              <span className="wh-stat-num">{lowStock}</span>
              <span className="wh-stat-lbl">{t('warehouse.outOfStock')}</span>
            </div>
          )}
        </div>
      </div>

      {err && <div className="wh-error"><Icon name="warning" size={15} /> {err}</div>}

      <div className="wh-tabs">
        {canIssue && (
          <button className={tab === 'pending' ? 'on' : ''} onClick={() => setTab('pending')}>
            <Icon name="clipboard" size={15} /> {t('warehouse.tabPending')}
            {pending.length > 0 && <span className="wh-badge">{pending.length}</span>}
          </button>
        )}
        <button className={tab === 'stock' ? 'on' : ''} onClick={() => setTab('stock')}>
          <Icon name="package" size={15} /> {t('warehouse.tabStock')}
        </button>
      </div>

      {/* ---------- İCRA GÖZLƏYƏN TƏLƏBNAMƏLƏR ---------- */}
      {tab === 'pending' && canIssue && (
        loadingPending ? <div className="wh-loading">{t('common.loading')}</div>
        : pending.length === 0 ? (
          <div className="wh-empty">
            <Icon name="checkCircle" size={34} />
            <p>{t('warehouse.noPending')}</p>
          </div>
        ) : (
          <div className="wh-req-list">
            {pending.map(r => {
              const open = openId === r.id;
              return (
                <div key={r.id} className={'wh-req' + (open ? ' open' : '')}>
                  <button className="wh-req-head" onClick={() => toggleOpen(r)}>
                    <span className="wh-req-num">{r.request_number}</span>
                    <span className="wh-req-dept">{r.department}</span>
                    <span className="wh-req-who">{r.requester_name}</span>
                    <span className="wh-req-cnt">{r.items.length} {t('warehouse.itemsShort')}</span>
                    <Icon name={open ? 'close' : 'arrowRight'} size={15} />
                  </button>

                  {open && (
                    <div className="wh-req-body">
                      <table className="wh-issue-table">
                        <thead>
                          <tr>
                            <th>{t('warehouse.colItem')}</th>
                            <th>{t('warehouse.colLocation')}</th>
                            <th>{t('warehouse.colOnHand')}</th>
                            <th>{t('warehouse.colRequested')}</th>
                            <th>{t('warehouse.colIssue')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.items.map(i => {
                            const qty = Number(issueQty[i.id] ?? 0);
                            const short = i.on_hand != null && qty > i.on_hand;
                            return (
                              <tr key={i.id}>
                                <td>
                                  <div className="wh-it-name">{i.item_description}</div>
                                  <div className="wh-it-code">
                                    {i.item_code || t('warehouse.noCode')}
                                    {!i.in_catalog && <span className="wh-tag-off">{t('warehouse.notInCatalog')}</span>}
                                  </div>
                                </td>
                                <td className="wh-it-loc">{i.location || '—'}</td>
                                <td>
                                  {i.on_hand == null
                                    ? <span className="wh-na">—</span>
                                    : <span className={'wh-onhand' + (i.on_hand <= 0 ? ' zero' : '')}>{i.on_hand}</span>}
                                </td>
                                <td className="wh-it-req">{i.quantity_requested} <small>{i.uom}</small></td>
                                <td>
                                  <input
                                    type="number" min="0" max={i.quantity_requested}
                                    className={'wh-qty' + (short ? ' over' : '')}
                                    value={issueQty[i.id] ?? 0}
                                    onChange={e => setIssueQty(p => ({ ...p, [i.id]: e.target.value }))}
                                  />
                                  {short && <div className="wh-warn"><Icon name="warning" size={11} /> {t('warehouse.overStock')}</div>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="wh-req-actions">
                        <span className="wh-hint">{t('warehouse.partialHint')}</span>
                        <button className="wh-btn-issue" disabled={issuing} onClick={() => submitIssue(r)}>
                          <Icon name="package" size={15} />
                          {issuing ? t('warehouse.issuing') : t('warehouse.btnIssue')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ---------- STOK ---------- */}
      {tab === 'stock' && (
        <>
          {canIssue && (
            <div className="wh-stock-actions">
              <button type="button" className="wh-act wh-act-primary" onClick={() => setImpOpen(true)}>
                <Icon name="file" size={15} /> {t('warehouse.importBtn')}
              </button>
              <button type="button" className="wh-act" onClick={() => { setNewItem(emptyItem); setAddOpen(true); }}>
                <Icon name="package" size={15} /> {t('warehouse.addItemBtn')}
              </button>
            </div>
          )}

          <div className="wh-search">
            <Icon name="search" size={15} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('warehouse.searchPlaceholder')}
            />
            {search && (
              <button onClick={() => setSearch('')} aria-label="Təmizlə"><Icon name="close" size={14} /></button>
            )}
          </div>

          {loadingStock ? <div className="wh-loading">{t('common.loading')}</div> : (
            <div className="wh-table-wrap">
              <table className="wh-stock-table">
                <thead>
                  <tr>
                    <th>{t('warehouse.colCode')}</th>
                    <th>{t('warehouse.colItem')}</th>
                    <th>{t('warehouse.colLocation')}</th>
                    <th>{t('warehouse.colBalance')}</th>
                    <th>{t('warehouse.colIssued')}</th>
                    <th>{t('warehouse.colReserved')}</th>
                    <th>{t('warehouse.colAvailable')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.length === 0 && (
                    <tr><td colSpan={7} className="wh-empty-row">{t('warehouse.noItems')}</td></tr>
                  )}
                  {stock.map(s => (
                    <tr key={s.id}>
                      <td className="wh-code">{s.item_code}</td>
                      <td className="wh-name">{s.name}</td>
                      <td className="wh-loc">{s.location || '—'}</td>
                      <td className="wh-num">{s.closing_qty} <small>{s.uom}</small></td>
                      <td className="wh-num muted">{s.issued_qty || '—'}</td>
                      <td className="wh-num muted">{s.reserved_qty || '—'}</td>
                      <td className={'wh-num strong' + (s.available <= 0 ? ' zero' : '')}>{s.available}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stock.length > 0 && (
                <p className="wh-note">
                  <Icon name="warning" size={12} /> {t('warehouse.balanceNote')}
                  {stock[0].balance_date ? ' (' + String(stock[0].balance_date).slice(0, 10) + ')' : ''}
                </p>
              )}
            </div>
          )}
        </>
      )}
      {/* ---------- Excel yükləmə ---------- */}
      {impOpen && (
        <div className="modal-overlay" onClick={resetImport}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{t('warehouse.importTitle')}</h3>
              <button onClick={resetImport}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <p className="wh-imp-hint">{t('warehouse.importHint')}</p>

              <label className="wh-drop">
                <input
                  type="file" accept=".xlsx,.xlsm,.xls" hidden
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setImpFile(f); doPreview(f, impMode); }
                  }}
                />
                <Icon name="file" size={22} />
                <span>{impFile ? impFile.name : t('warehouse.importPick')}</span>
              </label>

              <div className="wh-imp-modes">
                {['update', 'replace'].map(m => (
                  <label key={m} className={'wh-imp-mode' + (impMode === m ? ' on' : '')}>
                    <input type="radio" name="impmode" checked={impMode === m}
                           onChange={() => { setImpMode(m); if (impFile) doPreview(impFile, m); }} />
                    <b>{t('warehouse.mode_' + m)}</b>
                    <small>{t('warehouse.mode_' + m + '_desc')}</small>
                  </label>
                ))}
              </div>

              {impBusy && <div className="wh-loading">{t('common.loading')}</div>}

              {impPreview && !impBusy && (
                <div className="wh-imp-sum">
                  <div className="wh-imp-row">
                    <span>{t('warehouse.impTotal')}</span><b>{impPreview.summary.total}</b>
                  </div>
                  <div className="wh-imp-row">
                    <span>{t('warehouse.impNew')}</span><b className="ok">{impPreview.summary.new_items}</b>
                  </div>
                  <div className="wh-imp-row">
                    <span>{t('warehouse.impChanged')}</span><b className="warn">{impPreview.summary.changed}</b>
                  </div>
                  <div className="wh-imp-row">
                    <span>{t('warehouse.impSame')}</span><b>{impPreview.summary.unchanged}</b>
                  </div>
                  {impPreview.summary.deactivated > 0 && (
                    <div className="wh-imp-row">
                      <span>{t('warehouse.impDeactivated')}</span><b className="bad">{impPreview.summary.deactivated}</b>
                    </div>
                  )}
                  {impPreview.summary.error_count > 0 && (
                    <div className="wh-imp-errs">
                      <b>{t('warehouse.impErrors')}: {impPreview.summary.error_count}</b>
                      {impPreview.summary.errors.slice(0, 5).map((er, i) => (
                        <div key={i}>{t('warehouse.impRow')} {er.row} · {er.code} — {er.msg}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={resetImport}>{t('common.cancel')}</button>
                <button type="button" className="btn-primary" disabled={!impPreview || impBusy} onClick={doImport}>
                  {impBusy ? t('warehouse.importing') : t('warehouse.importApply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Tək material ---------- */}
      {addOpen && (
        <div className="modal-overlay" onClick={() => setAddOpen(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{t('warehouse.addItemTitle')}</h3>
              <button onClick={() => setAddOpen(false)}><Icon name="close" size={16} /></button>
            </div>
            <form className="modal-body" onSubmit={doAddItem}>
              <label>{t('warehouse.colCode')} *</label>
              <input value={newItem.item_code} required
                     onChange={e => setNewItem(p => ({ ...p, item_code: e.target.value }))}
                     placeholder="məs: 1187" />

              <label>{t('warehouse.colItem')} *</label>
              <input value={newItem.name} required
                     onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                     placeholder={t('warehouse.addNamePh')} />

              <div className="wh-add-row">
                <div>
                  <label>{t('warehouse.colOnHand')} *</label>
                  <input type="number" min="0" step="any" value={newItem.closing_qty} required
                         onChange={e => setNewItem(p => ({ ...p, closing_qty: e.target.value }))} />
                </div>
                <div>
                  <label>Ö/V</label>
                  <select value={newItem.uom} onChange={e => setNewItem(p => ({ ...p, uom: e.target.value }))}>
                    {['ədəd', 'bağlama', 'rulon', 'dəst', 'metr', 'kq', 'litr', 'paket', 'qutu'].map(u =>
                      <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <label>{t('warehouse.colLocation')}</label>
              <input value={newItem.location}
                     onChange={e => setNewItem(p => ({ ...p, location: e.target.value }))}
                     placeholder="məs: 9 shelf" />

              <p className="wh-imp-hint">{t('warehouse.addHint')}</p>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setAddOpen(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={addBusy}>
                  {addBusy ? t('warehouse.importing') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
